import { useState, useEffect, useCallback } from 'react';

import { LoadableState } from '../../../../shared/types/LoadableState';
import * as PartnersRepo from '../../domain/repo/PartnersRepo';
import { AttachVehicleFormModel } from '../../domain/repo/types/AttachVehicleFormModel';
import { AttachVehicleFormResponseModel } from '../../domain/repo/types/AttachVehicleFormResponseModel';
import { cityToGeoRegionId } from '../../domain/repo/types/CityConfig';
import { PartnerCity } from '../../domain/repo/types/PartnerCity';
import { PartnerVehicle } from '../../domain/repo/types/PartnerVehicle';
import { getInfoSourceList, getServicableCities } from '../helpers/PartnersHelper';

interface UsePartnerFormReturn {
  city: string;
  setCity: (city: string) => void;
  vehicles: LoadableState<PartnerVehicle[]>;
  formSubmitStatus: LoadableState<AttachVehicleFormResponseModel>;
  partnerCities: PartnerCity[];
  infoSourceList: string[];
  submitFormData: (formData: AttachVehicleFormModel) => Promise<void>;
  resetForm: () => void;
}

export function usePartnerForm(defaultCity: string): UsePartnerFormReturn {
  const [city, setCity] = useState<string>(defaultCity);
  const [vehicles, setVehicles] = useState<LoadableState<PartnerVehicle[]>>({
    status: 'init',
  });
  const [formSubmitStatus, setFormSubmitStatus] = useState<
    LoadableState<AttachVehicleFormResponseModel>
  >({ status: 'init' });

  const partnerCities: PartnerCity[] = getServicableCities().map((c) => ({
    id: cityToGeoRegionId(c),
    name: c,
  }));

  const infoSourceList = getInfoSourceList();

  const fetchPartnerVehicles = useCallback(
    async (cityName: string): Promise<void> => {
      setVehicles({ status: 'loading' });
      try {
        const data = await PartnersRepo.fetchPartnerVehicles(
          cityToGeoRegionId(cityName),
        );
        setVehicles({ status: 'live', data });
      } catch (error) {
        console.error('Error loading vehicles:', error);
        setVehicles({ status: 'error', message: 'Failed to load vehicles' });
      }
    },
    [],
  );

  const submitFormData = async (
    formData: AttachVehicleFormModel,
  ): Promise<void> => {
    setFormSubmitStatus({ status: 'loading' });
    try {
      const data = await PartnersRepo.submitAttachVehicleForm(formData);
      setFormSubmitStatus({ status: 'live', data });
    } catch {
      setFormSubmitStatus({
        status: 'error',
        message: 'Failed to submit form',
      });
    }
  };

  const resetForm = (): void => {
    setFormSubmitStatus({ status: 'init' });
  };

  useEffect(() => {
    fetchPartnerVehicles(city);
  }, [city, fetchPartnerVehicles]);

  return {
    city,
    setCity,
    vehicles,
    formSubmitStatus,
    partnerCities,
    infoSourceList,
    submitFormData,
    resetForm,
  };
}
